import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { ChevronDown, ChevronRight, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PracticeQuestionCard, {
  type AnswerState,
} from '@/components/practice/PracticeQuestionCard';
import type { Question } from '@/types/database';
import type { FriendlyError } from '@/lib/errors/types';

const TRANSITION_MS = 380;
const TOTAL_QUESTIONS = 128;

interface QuestionRowProps {
  question: Question;
  isLocked: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  inputMode: 'voice' | 'text';
  onInputModeChange: (mode: 'voice' | 'text') => void;
  answerState: AnswerState;
  onAnswer: (text: string) => void;
  onError: (err: FriendlyError) => void;
  onReveal: () => void;
  onNext: () => void;
  hasNext: boolean;
}

export default function QuestionRow({
  question,
  isLocked,
  isExpanded,
  onToggle,
  inputMode,
  onInputModeChange,
  answerState,
  onAnswer,
  onError,
  onReveal,
  onNext,
  hasNext,
}: QuestionRowProps) {
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);
  const [renderExpanded, setRenderExpanded] = useState(isExpanded);

  // Mount expanded body immediately when opening (derived during render).
  if (isExpanded && !renderExpanded) {
    setRenderExpanded(true);
  }

  useEffect(() => {
    if (isExpanded) {
      const id = requestAnimationFrame(() =>
        cardRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        }),
      );
      return () => cancelAnimationFrame(id);
    }
    if (!renderExpanded) return;
    const t = window.setTimeout(() => setRenderExpanded(false), TRANSITION_MS);
    return () => window.clearTimeout(t);
  }, [isExpanded, renderExpanded]);

  if (isLocked) {
    return (
      <LockedRow question={question} onUpgrade={() => navigate('/pricing')} />
    );
  }

  const handleHeaderKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <div
      ref={cardRef}
      className={`overflow-hidden rounded-xl border bg-card transition-[border-color,box-shadow,background-color] duration-300 ease-out ${
        isExpanded
          ? 'border-primary/40 shadow-md'
          : 'border-border shadow-sm hover:bg-cream/60'
      }`}
    >
      {/* Header: collapsed = full row, expanded = slim wayfinder strip */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onClick={onToggle}
        onKeyDown={handleHeaderKeyDown}
        className={`group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
          isExpanded
            ? 'px-5 py-2.5 md:px-7 bg-cream-warm/40 border-b border-border/40'
            : 'p-4 md:p-5'
        }`}
      >
        {isExpanded ? (
          <div className="flex items-center justify-between gap-4">
            <span className="font-sans text-[11px] font-medium uppercase tracking-[0.22em] text-text-muted">
              Question {question.id}{' '}
              <span className="text-text-muted/50">/ {TOTAL_QUESTIONS}</span>
            </span>
            <ChevronDown
              size={18}
              aria-hidden
              className="flex-shrink-0 text-primary group-hover:text-primary-dark transition-colors"
            />
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-light text-primary font-sans font-semibold text-sm flex items-center justify-center">
              {question.id}
            </span>

            <div className="flex-1 min-w-0">
              <span className="inline-block font-sans text-[10px] font-semibold uppercase tracking-[0.18em] bg-primary-light/60 text-primary px-2 py-0.5 rounded-full mb-1.5">
                {question.category}
              </span>
              <p className="font-display text-base md:text-lg text-text-primary leading-snug truncate">
                {question.question_text}
              </p>
            </div>

            <ChevronRight
              size={20}
              aria-hidden
              className="flex-shrink-0 text-text-muted group-hover:text-primary transition-transform duration-300 ease-out motion-reduce:transition-none"
            />
          </div>
        )}
      </div>

      {/* Expanded body — full-bleed banded zones rendered by PracticeQuestionCard */}
      <div
        className={`grid transition-[grid-template-rows] duration-[380ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          {renderExpanded && (
            <div
              className={`transition-opacity duration-300 ease-out motion-reduce:transition-none ${
                isExpanded ? 'opacity-100 delay-100' : 'opacity-0'
              }`}
            >
              <PracticeQuestionCard
                question={question}
                inputMode={inputMode}
                onInputModeChange={onInputModeChange}
                answerState={answerState}
                onAnswer={onAnswer}
                onError={onError}
                onReveal={onReveal}
                onClose={onToggle}
                onNext={onNext}
                hasNext={hasNext}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface LockedRowProps {
  question: Question;
  onUpgrade: () => void;
}

function LockedRow({ question, onUpgrade }: LockedRowProps) {
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onUpgrade();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onUpgrade}
      onKeyDown={handleKeyDown}
      aria-label="Upgrade to Premium to unlock this question"
      className="group relative flex items-center gap-4 rounded-xl border border-border bg-card p-4 md:p-5 cursor-pointer overflow-hidden transition-colors hover:bg-cream-warm/40 focus:outline-none focus:ring-2 focus:ring-primary/50"
    >
      <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-light/70 text-primary font-sans font-semibold text-sm flex items-center justify-center">
        {question.id}
      </span>

      <div className="flex-1 min-w-0">
        <span className="inline-block font-sans text-[10px] font-semibold uppercase tracking-[0.18em] bg-primary-light/60 text-primary px-2 py-0.5 rounded-full mb-1.5">
          {question.category}
        </span>
        <p
          aria-hidden
          className="font-display text-base md:text-lg text-text-primary leading-snug truncate select-none pointer-events-none filter blur-[5px]"
        >
          {question.question_text}
        </p>
      </div>

      <span className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-cream-warm/60 px-2.5 py-1 font-sans text-[11px] font-semibold uppercase tracking-[0.14em] text-primary group-hover:border-primary/60 group-hover:bg-primary group-hover:text-white transition-colors">
        <Lock size={11} className="group-hover:hidden" />
        <span className="group-hover:hidden">Premium</span>
        <span className="hidden group-hover:inline">Unlock →</span>
      </span>
    </div>
  );
}
